import classNames from "classnames"
import utcToZonedTime from "date-fns-tz/utcToZonedTime"
import eachMinuteOfInterval from "date-fns/eachMinuteOfInterval"
import format from "date-fns/format"
import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Controller, useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"

import AlertBanner from "src/components/AlertBanner"
import Form from "src/components/Form"
import Modal from "src/components/Modal"
import Tooltip from "src/components/Tooltip"

import {
  createScheduledLaunch,
  queryBookableScheduleAvailability,
  queryLaunchAddOnEstimate,
  queryLaunchAddOns,
  queryReservationSearch,
  updateScheduledLaunch,
} from "src/api/DryStack"

import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { availableTime } from "src/utils/form/available_time"
import { titlecase } from "src/utils/string_helpers"

import LaunchAddOnFields from "../AddOns/LaunchAddOnFields"
import RequestedAddOns, { hasRequestedAddOns } from "../AddOns/RequestedAddOns"
import {
  hasIncompleteAddOnSelection,
  isSubmittable,
} from "../AddOns/launchAddOnPricing"
import { useLaunchAddOnEstimate } from "../AddOns/useLaunchAddOnEstimate"
import { MarinaScheduleContext } from "./MarinaScheduleContext"
import { validateFutureTime } from "./MarinaScheduleSharedMethods"

const ScheduleAppointmentModal = ({
  closeModal,
  currentDate,
  isOpen,
  marinaSlug,
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const hasInitializedForm = useRef(false)
  const addOnsSectionRef = useRef(null)
  const {
    currentAppointment,
    currentTabName,
    marinaTimezone,
    setCurrentAppointment,
    trackingProps,
  } = useContext(MarinaScheduleContext)
  const queryClient = useQueryClient()
  const {
    register,
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date: currentDate,
      estimatedReturn: null,
      location: "",
      note: "",
      reservation: null,
      time: new Date(),
      enableEmailNotification: true,
      addOns: [],
    },
  })
  const addOns = watch("addOns")

  const {
    isError,
    isLoading,
    data: reservationSearchData,
  } = useQuery(
    ["reservationSearch", searchQuery],
    () => queryReservationSearch(marinaSlug, searchQuery),
    { refetchOnWindowFocus: false, enabled: Boolean(searchQuery) }
  )

  const { data: addOnCatalog } = useQuery(
    ["launchAddOns", marinaSlug],
    () => queryLaunchAddOns(marinaSlug),
    { refetchOnWindowFocus: false, retry: false, enabled: !currentAppointment }
  )

  const addOnCatalogItems = useMemo(
    () =>
      (addOnCatalog ?? []).map((product) => ({
        productId: product.id,
        name: product.name,
        productType: product.productType,
        fuel: product.fuel,
        pricePerUnit: product.pricePerUnit,
        pricePrecision: product.pricePrecision,
        pricePerUnitFormatted: product.pricePerUnitFormatted,
        outOfStock: product.outOfStock,
      })),
    [addOnCatalog]
  )

  // A live quote for the marina staff scheduling a launch, same as the
  // boater's own pre-submit estimate — the Subtotal/Tax/Estimated total block
  // isn't in the Desktop - Cart designs, but the backend #estimate endpoint
  // already exists for this surface (Manage::DryStack::LaunchAddonsController)
  // and every other add-on selection form in the app renders it.
  const addOnEstimate = useLaunchAddOnEstimate({
    catalog: addOnCatalogItems,
    value: addOns,
    enabled: !currentAppointment,
    fetchEstimate: (selection) =>
      queryLaunchAddOnEstimate(marinaSlug, selection),
  })

  const {
    data: bookableScheduleAvailabilityData,
    isFetching: fetchingAvailability,
  } = useQuery(
    [
      "bookableScheduleAvailability",
      marinaSlug,
      watch("date") && format(watch("date"), "yyyy-MM-dd"),
    ],
    () =>
      queryBookableScheduleAvailability(
        marinaSlug,
        watch("date") && format(watch("date"), "yyyy-MM-dd")
      ),
    {
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

  const generateTimeOptions = useCallback(() => {
    const options = []
    const formattedDate = format(currentDate, "yyyy-MM-dd")
    const operatingHoursBounds =
      bookableScheduleAvailabilityData.operatingHoursBounds
    const interval = bookableScheduleAvailabilityData.eventDuration

    if (operatingHoursBounds && operatingHoursBounds.length === 2) {
      const [startTime, endTime] = operatingHoursBounds
      const start = new Date(`${formattedDate} ${startTime}`)
      const end = new Date(`${formattedDate} ${endTime}`)

      const timeIntervals = eachMinuteOfInterval(
        { start, end },
        { step: interval }
      )

      timeIntervals.forEach((time) => {
        options.push(time)
      })
    } else {
      const start = new Date(`${formattedDate} 00:00`)
      const end = new Date(`${formattedDate} 24:00`)

      const timeIntervals = eachMinuteOfInterval(
        { start, end },
        { step: interval }
      )

      timeIntervals.forEach((time) => {
        options.push(time)
      })
    }

    return options
  }, [bookableScheduleAvailabilityData, currentDate])

  const getDefaultTimeValue = useCallback(() => {
    const timeOptions = generateTimeOptions()
    const availableTimes =
      bookableScheduleAvailabilityData?.availableTimes || []
    const currentMarinaTime = utcToZonedTime(new Date(), marinaTimezone)

    // Helper to check if a time is in availableTimes
    const isTimeAvailable = (time) => {
      const timeString = format(time, "HH:mm")
      return availableTimes.includes(timeString)
    }

    // First, try to find a future time that is also available
    const futureAvailableTime = timeOptions.find((time) => {
      return time > currentMarinaTime && isTimeAvailable(time)
    })
    if (futureAvailableTime) {
      return futureAvailableTime
    }

    // If no future available time, find any future time (for override capability)
    const futureTime = timeOptions.find((time) => time > currentMarinaTime)
    if (futureTime) {
      return futureTime
    }

    // If all times are in the past, prefer first available time over first time option
    const firstAvailableTime = timeOptions.find((time) => isTimeAvailable(time))
    if (firstAvailableTime) {
      return firstAvailableTime
    }

    // Last resort fallback
    return timeOptions[0]
  }, [generateTimeOptions, marinaTimezone, bookableScheduleAvailabilityData])

  const updateSearchQuery = (query) => {
    setSearchQuery(query)
  }

  useEffect(() => {
    if (
      isOpen &&
      bookableScheduleAvailabilityData &&
      !hasInitializedForm.current
    ) {
      hasInitializedForm.current = true
      if (currentAppointment) {
        const reservation = {
          contactBoat: currentAppointment.contactBoat,
          contact: currentAppointment.contact,
          encodedId: currentAppointment.reservationEncodedId,
        }
        setValue(
          "time",
          utcToZonedTime(
            currentAppointment.startTime,
            currentAppointment.marinaTimezone
          )
        )
        setValue("reservation", reservation)
        setValue("location", currentAppointment.location)
        setValue("note", currentAppointment.note)
        setValue(
          "date",
          utcToZonedTime(
            currentAppointment.startTime,
            currentAppointment.marinaTimezone
          )
        )
        if (currentAppointment.estimatedReturnTime) {
          setValue(
            "estimatedReturn",
            utcToZonedTime(
              currentAppointment.estimatedReturnTime,
              currentAppointment.marinaTimezone
            )
          )
        }
      } else {
        if (bookableScheduleAvailabilityData.availableLocations) {
          setValue(
            "location",
            bookableScheduleAvailabilityData.availableLocations[0]
          )
        } else {
          setValue(
            "location",
            bookableScheduleAvailabilityData.disabledLocations[0]
          )
        }
        setValue("time", getDefaultTimeValue())
        setValue("date", currentDate)
      }
    }
  }, [
    currentAppointment,
    currentDate,
    getDefaultTimeValue,
    setValue,
    isOpen,
    bookableScheduleAvailabilityData,
  ])

  const [debouncedUpdateSearchQuery] = useDebounce(updateSearchQuery)

  const onInputChange = useCallback(
    (value) => {
      debouncedUpdateSearchQuery(value)
    },
    [debouncedUpdateSearchQuery]
  )

  const showToast = useToast()
  const tracker = useTracker()

  const trackFormSubmitEvent = () => {
    const note = getValues("note")
    let eventVariant
    let createNoteEvent

    if (currentAppointment) {
      eventVariant = `${currentTabName()}_tab_edit`
      createNoteEvent = !!note && !currentAppointment.note
    } else {
      eventVariant = "new"
      createNoteEvent = !!note
    }

    tracker.trackEvent(`dry_stack_schedule_view:${eventVariant}_launch_saved`, {
      ...trackingProps,
    })

    if (createNoteEvent) {
      tracker.trackEvent(
        `dry_stack_schedule_view:${eventVariant}_launch_note_added`,
        {
          ...trackingProps,
        }
      )
    }
  }

  // An unresolved fuel line rolls the whole ScheduleAppointment.transaction
  // back server-side with nothing pointing at the field (:fuel_mode isn't in
  // BOATER_SAFE_ERRORS) — the addOns Controller's validate rule stops that
  // reaching submit. This only has to get staff's eyes to the reason: the
  // Add-Ons section sits mid-form in a scrolling body.
  const handleInvalidSubmit = (formErrors) => {
    if (formErrors.addOns) {
      addOnsSectionRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      })
    }
  }

  const handleFormSubmit = (data) => {
    const formData = {
      reservation_id: data.reservation.encodedId,
      note: data.note,
      start_time: format(data.time, "HH:mm"),
      start_date: format(data.date, "yyyy-MM-dd"),
      location: data.location,
      estimated_return_time:
        data.estimatedReturn &&
        format(
          utcToZonedTime(data.estimatedReturn, marinaTimezone),
          "yyyy-MM-dd HH:mm"
        ),
    }

    if (!currentAppointment) {
      formData.enable_email_notification = data.enableEmailNotification
      // The Controller's validate rule already blocks submission on any
      // non-submittable selection; filtering the payload through the same
      // predicate keeps this from silently drifting out of sync with it.
      formData.addons = data.addOns
        .filter((addOn) =>
          isSubmittable(
            addOnCatalogItems.find(
              (item) => item.productId === addOn.productId
            ),
            addOn
          )
        )
        .map((addOn) => ({
          product_id: addOn.productId,
          quantity: addOn.quantity,
          fuel_mode: addOn.fuelMode,
        }))
    }

    trackFormSubmitEvent()

    if (currentAppointment) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleSuccess = () => {
    queryClient.invalidateQueries(["scheduledLaunches"])
    queryClient.invalidateQueries(["scheduleAppointmentsCount", marinaSlug])
    handleClose()
  }

  const createMutation = useMutation(
    (formState) => createScheduledLaunch(marinaSlug, formState),
    {
      onSuccess: () => {
        showToast("New launch created", { type: "success" })
        handleSuccess()
      },
    }
  )

  const updateMutation = useMutation(
    (formState) =>
      updateScheduledLaunch(
        marinaSlug,
        formState,
        currentAppointment.encodedId
      ),
    {
      onSuccess: () => {
        showToast("Launch updated", { type: "success" })
        handleSuccess()
      },
    }
  )

  const filterTime = (time) => {
    if (bookableScheduleAvailabilityData) {
      const operatingHoursBounds =
        bookableScheduleAvailabilityData.operatingHoursBounds

      if (operatingHoursBounds && operatingHoursBounds.length === 2) {
        const timeHour = time.getHours()
        const timeMinutes = time.getMinutes()

        const [startTimeStr, endTimeStr] = operatingHoursBounds

        const startingTimes = startTimeStr.split(":")
        const startingHour = parseInt(startingTimes[0])
        const startingMinutes = parseInt(startingTimes[1])

        const endingTimes = endTimeStr.split(":")
        const endingHour = parseInt(endingTimes[0])
        const endingMinutes = parseInt(endingTimes[1])

        const isAfterStart =
          timeHour > startingHour ||
          (timeHour === startingHour && timeMinutes >= startingMinutes)
        const isBeforeEnd =
          timeHour < endingHour ||
          (timeHour === endingHour && timeMinutes <= endingMinutes)

        const isWithinBounds = isAfterStart && isBeforeEnd

        if (isWithinBounds) {
          return generateTimeOptions().some(
            (generatedTime) =>
              generatedTime.getHours() === timeHour &&
              generatedTime.getMinutes() === timeMinutes
          )
        } else {
          return false
        }
      }
    }

    return true
  }

  const handleClose = () => {
    updateMutation.reset()
    createMutation.reset()
    setCurrentAppointment(null)
    reset()
    hasInitializedForm.current = false
    closeModal()
  }

  const submitButtonText = () => {
    if (currentAppointment) {
      if (updateMutation.isLoading) {
        return "Saving Changes"
      } else {
        return "Save Changes"
      }
    } else {
      if (createMutation.isLoading) {
        return "Saving"
      } else {
        return "Save"
      }
    }
  }

  const renderLocationOptions = () => {
    const mergedLocations =
      bookableScheduleAvailabilityData.availableLocations.concat(
        bookableScheduleAvailabilityData.disabledLocations
      )

    return mergedLocations.map((location, index) => {
      const isDisabled =
        bookableScheduleAvailabilityData.disabledLocations.includes(location)

      return (
        <Form.Select.RichOption key={index} value={location} id={index}>
          <span className={classNames({ "text-muted": isDisabled })}>
            {titlecase(location)}
          </span>
        </Form.Select.RichOption>
      )
    })
  }

  const renderSearchOptions = () => {
    if (isLoading) {
      return <div className="select-none px-4 py-2">Loading reservations</div>
    } else if (isError) {
      return (
        <div className="select-none px-4 py-2">
          There was an error searching for reservations
        </div>
      )
    } else if (
      reservationSearchData?.reservations?.length === 0 &&
      searchQuery !== ""
    ) {
      return (
        <div className="select-none px-4 py-2">
          No matching active reservations found
        </div>
      )
    } else {
      return reservationSearchData?.reservations.map((reservation, index) => {
        return (
          <Form.Search.Option
            key={`reservation-search-${index}`}
            id={`reservation-search-${index}`}
            value={reservation}
          >
            <div className="flex flex-col px-2">
              <span className="text-lg">{reservation.contactBoat.name}</span>
              <div className="text-muted flex w-full justify-between">
                <span>Captain: {reservation.contact.name}</span>
                <span>Res ID: #{reservation.encodedId}</span>
              </div>
            </div>
          </Form.Search.Option>
        )
      })
    }
  }

  const renderAvailabilityLoadingStatus = () => (
    <div className="col-span-12 flex items-center justify-center md:col-span-8">
      <div className="mt-6">
        <i className="fa-solid fa-spinner-third fa-spin mr-3" />
        <span>Loading availability</span>
      </div>
    </div>
  )

  const renderAvailabilityInputs = () => (
    <>
      <div className="col-span-12 md:col-span-4">
        <div className="flex">
          <Form.Label htmlFor="schedule-appointment-time">Time</Form.Label>
          <Tooltip
            text="Times shown in grey are outside operating hours or fully booked per your dry stack availability settings, but you can still choose to override and select these times at your discretion"
            placement="bottom"
            maxWidth="250px"
            variant="dark"
          >
            <i className="fa-solid fa-circle-info ml-2 cursor-pointer text-gray-600" />
          </Tooltip>
        </div>
        <Controller
          name={"time"}
          control={control}
          rules={{
            validate: (value) => {
              if (!bookableScheduleAvailabilityData) {
                return "Time must be one of the selectable values"
              }
              if (
                !currentAppointment &&
                !validateFutureTime(value, getValues("date"), marinaTimezone)
              ) {
                return "Time must be in the future"
              }
              return true
            },
          }}
          render={({ field: { onChange, value } }) => (
            <Form.TimePicker
              id="schedule-appointment-time"
              hasErrors={!!errors?.time}
              disabled={fetchingAvailability}
              availableTime={(time) =>
                availableTime(
                  time,
                  bookableScheduleAvailabilityData.availableTimes
                )
              }
              filterTime={filterTime}
              injectTimes={generateTimeOptions()}
              onFocus={(event) => (event.target.readOnly = true)}
              {...{ onChange, value }}
            />
          )}
        />
        {errors?.time && <Form.Error>{errors.time.message}</Form.Error>}
      </div>
      <div className="col-span-12 md:col-span-4">
        <Form.Label htmlFor="schedule-appointment-location">
          Location
        </Form.Label>
        <Controller
          control={control}
          name="location"
          render={({ field: { onChange } }) => (
            <Form.Select.Custom
              id="schedule-appointment-location"
              onChange={(value) => {
                onChange(value)
              }}
              disabled={fetchingAvailability}
              value={getValues("location")}
            >
              {renderLocationOptions()}
            </Form.Select.Custom>
          )}
        />
      </div>
    </>
  )

  const renderReservationSearchForm = () => (
    <Controller
      name="reservation"
      control={control}
      rules={{
        required: "A reservation is required to schedule a launch",
      }}
      render={({ field: { onChange, value } }) => (
        <Form.Search
          data={reservationSearchData?.reservations}
          value={value}
          onChange={onChange}
          query={searchQuery}
          setQuery={onInputChange}
          hasErrors={!!errors.reservation}
          placeholder="Start typing a boat name, captain, reservation id, or space name to search for a reservation"
        >
          {renderSearchOptions()}
        </Form.Search>
      )}
    />
  )

  const renderSelectedReservationBanner = (value) => (
    <div className="relative mb-1 flex w-full rounded bg-teal-100 p-2">
      {!currentAppointment && (
        <button
          className="fa-solid fa-xmark absolute right-2 bg-transparent px-0 py-1 text-xxs text-gray-600"
          onClick={() => {
            setValue("reservation", null)
            clearErrors()
          }}
        />
      )}
      <div className="w-11/12 overflow-hidden truncate">
        <span className="mr-2 font-semibold">Selected Reservation:</span>
        <span>
          {value.contactBoat.name}, Captain: {value.contact.name}, Res ID: #
          <a href={reservationUrl(value)}>{value.encodedId}</a>
        </span>
      </div>
    </div>
  )

  const renderEstimatedReturnInput = () => (
    <div className={classNames("col-span-12", { "pb-6": currentAppointment })}>
      <Form.Label htmlFor="estimated-return" optional>
        Estimated Return
      </Form.Label>
      <Controller
        name={"estimatedReturn"}
        control={control}
        rules={{
          validate: (value) => {
            if (value && !validateFutureTime(value, value, marinaTimezone)) {
              return "Estimated return must be in the future"
            }
            if (value && value < getValues("time")) {
              return "Estimated return must be after start time"
            }
            return true
          },
        }}
        render={({ field: { onChange, value } }) => (
          <Form.DatePicker
            id="estimated-return"
            {...{ onChange, value }}
            hasErrors={!!errors?.estimatedReturn}
            minDate={new Date()}
            maxDate={
              getValues("reservation") &&
              parseISO(getValues("reservation").checkOutDate)
            }
            showTimeSelect
            isClearable
            timeIntervals={30}
            dateFormat="MM/dd/yyyy 'at' h:mm aa"
            placeholderText="Select a date and time"
            onFocus={(event) => (event.target.readOnly = true)}
          />
        )}
      />
      {errors?.estimatedReturn && (
        <Form.Error>{errors.estimatedReturn.message}</Form.Error>
      )}
    </div>
  )

  const renderNoteInput = () => (
    <div className="col-span-12">
      <Form.Label htmlFor="schedule-appointment-note">Notes</Form.Label>
      <Form.Textarea
        {...register("note")}
        placeholder="Add notes"
        id="schedule-appointment-note"
      />
      <Form.Subtext>
        Boaters have access to their launch details and can see any notes
        entered here
      </Form.Subtext>
    </div>
  )

  const renderDateInput = () => (
    <div className="col-span-12 md:col-span-4">
      <Form.Label htmlFor="schedule-appointment-date">Date</Form.Label>
      <Controller
        name={"date"}
        control={control}
        render={({ field: { onChange, value } }) => (
          <Form.DatePicker
            id="schedule-appointment-date"
            {...{ onChange, value }}
            minDate={new Date()}
            maxDate={
              getValues("reservation") &&
              parseISO(getValues("reservation").checkOutDate)
            }
            onFocus={(event) => (event.target.readOnly = true)}
          />
        )}
      />
    </div>
  )

  const renderEnableEmailNotificationInput = () => (
    <div className="col-span-12 pb-6">
      <Form.Checkbox
        {...register("enableEmailNotification")}
        label="Send launch confirmation email to boater"
      />
    </div>
  )

  // Add-ons are locked once a launch is scheduled (MOB-1022 AC), so this only
  // ever renders for a brand-new launch, never while editing currentAppointment.
  const renderAddOnFields = () => (
    <div className="col-span-12" ref={addOnsSectionRef}>
      <Form.Label>Add-Ons</Form.Label>
      <Form.Subtext>Anything to have ready for them on arrival?</Form.Subtext>
      <Controller
        name="addOns"
        control={control}
        rules={{
          validate: (value) =>
            !hasIncompleteAddOnSelection(addOnCatalogItems, value) ||
            "Complete or remove the highlighted add-on to continue.",
        }}
        render={({ field }) => (
          <LaunchAddOnFields
            catalog={addOnCatalogItems}
            value={field.value}
            onChange={field.onChange}
            estimate={addOnEstimate.estimate}
            estimateFailed={addOnEstimate.failed}
            validationAttempted={Boolean(errors.addOns)}
          />
        )}
      />
      {errors?.addOns && <Form.Error>{errors.addOns.message}</Form.Error>}
    </div>
  )

  const reservationUrl = (value) => {
    return (
      process.env.BASE_URL +
      "/manage/" +
      marinaSlug +
      "/reservations/" +
      value.encodedId
    )
  }

  const renderModalBody = () => (
    <Modal.Body>
      {(createMutation.isError || updateMutation.isError) && (
        <div className="mb-4 flex w-full">
          <Form.Error>
            There was a problem {currentAppointment ? "updating" : "scheduling"}{" "}
            your launch, please try again or contact mayday@dockwa.com for
            further assistance.
          </Form.Error>
        </div>
      )}
      {watch("reservation")?.contact?.hasOutstandingBalance && (
        <div className="mb-4">
          <AlertBanner variant="warning">
            This boater has an outstanding balance.{" "}
            <a
              href={`/manage/${marinaSlug}/contacts/${
                watch("reservation")?.contact?.encodedId
              }#invoices`}
              className="font-semibold text-blue-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              View their invoices
            </a>
          </AlertBanner>
        </div>
      )}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <Form.Label htmlFor="schedule-appointment-reservation">
            Reservation
          </Form.Label>
          {watch("reservation")
            ? renderSelectedReservationBanner(getValues("reservation"))
            : renderReservationSearchForm()}
          {errors?.reservation && (
            <Form.Error>{errors.reservation.message}</Form.Error>
          )}
        </div>
        {renderDateInput()}
        {fetchingAvailability || !bookableScheduleAvailabilityData
          ? renderAvailabilityLoadingStatus()
          : renderAvailabilityInputs()}
        {!currentAppointment && addOnCatalog?.length > 0 && renderAddOnFields()}
        {currentAppointment &&
          hasRequestedAddOns(
            currentAppointment.addons,
            currentAppointment.addonEstimate
          ) && (
            <div className="col-span-12" data-testid="requested-addons-wrapper">
              <RequestedAddOns
                addons={currentAppointment.addons}
                addonEstimate={currentAppointment.addonEstimate}
              />
            </div>
          )}
        {renderNoteInput()}
        {renderEstimatedReturnInput()}
        {!currentAppointment && renderEnableEmailNotificationInput()}
      </div>
    </Modal.Body>
  )

  const renderModalFooter = () => (
    <Modal.Footer
      secondaryOnSubmit={handleClose}
      secondaryBtnText="Cancel"
      secondaryBtnVariant="tertiary"
      confirmBtnText={submitButtonText()}
      confirmBtnLoading={createMutation.isLoading || updateMutation.isLoading}
      confirmBtnType="submit"
      disabled={createMutation.isLoading || updateMutation.isLoading}
    />
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header
        title={
          currentAppointment ? "Edit Launch Details" : "Schedule New Launch"
        }
      />
      <div className="overflow-y-auto overflow-x-hidden">
        <Form onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}>
          {renderModalBody()}
          {renderModalFooter()}
        </Form>
      </div>
    </Modal>
  )
}

ScheduleAppointmentModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  currentDate: PropTypes.instanceOf(Date),
  isOpen: PropTypes.bool.isRequired,
  marinaSlug: PropTypes.string.isRequired,
}

export default ScheduleAppointmentModal
